import React, { useState, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { useScrollReveal, useInViewport } from '../../hooks/useScrollAnimations';
import { Users, Heart, Search, Lightbulb } from 'lucide-react';

interface JourneyStep {
  id: number;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  description: string;
  learnings: string[];
  imagePlaceholder: string;
}

const journeySteps: JourneyStep[] = [
  {
    id: 1,
    icon: <Users size={32} />,
    title: "Understanding the Community",
    subtitle: "Week 1-2: Immersion & Context Building",
    description: "We didn't start with surveys or interviews. We started by showing up. Volunteering at distribution days, helping pack boxes, learning names. The research began by earning the right to ask questions.",
    learnings: [
      "Observed 6 food distribution sessions",
      "Volunteered 40+ hours alongside staff",
      "Built relationships before extracting data",
      "Learned operational constraints firsthand"
    ],
    imagePlaceholder: "📷 Photos from volunteering sessions / community engagement"
  },
  {
    id: 2,
    icon: <Heart size={32} />,
    title: "Building Trust",
    subtitle: "Week 3-4: Trauma-Informed Approach",
    description: "Traditional consent forms felt transactional. We redesigned our entire research protocol through a trauma-informed lens—centering dignity, transparency, and participant agency at every step.",
    learnings: [
      "Developed visual consent materials in 3 languages",
      "Created safe, private interview spaces",
      "Offered flexible participation options",
      "Partnered with community liaisons"
    ],
    imagePlaceholder: "📷 Research materials / trauma-informed consent forms"
  },
  {
    id: 3,
    icon: <Search size={32} />,
    title: "Research in Action",
    subtitle: "Week 5-8: Mixed-Method Data Collection",
    description: "We combined observational research, contextual interviews, and participatory mapping sessions. Each method was adapted to honor participants' time, energy, and lived expertise.",
    learnings: [
      "Conducted 25 in-depth interviews",
      "Facilitated 4 community mapping sessions",
      "Documented journey maps with participants",
      "Compensated participants for their time"
    ],
    imagePlaceholder: "📷 Research sessions / journey mapping activities"
  },
  {
    id: 4,
    icon: <Lightbulb size={32} />,
    title: "Synthesis & Insights",
    subtitle: "Week 9-10: Making Sense Together",
    description: "Analysis wasn't something we did in isolation. We brought findings back to the community, validating insights and co-creating recommendations that reflected their priorities, not just ours.",
    learnings: [
      "Identified 12 critical mobility barriers",
      "Mapped service ecosystem gaps",
      "Co-created solution frameworks",
      "Presented findings to community partners"
    ],
    imagePlaceholder: "📷 Analysis artifacts / synthesis boards / community presentations"
  }
];

const JourneySection: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const { ref: titleRef, isInView: titleInView } = useScrollReveal(0.3);
  const { ref: timelineRef, isInView: timelineInView } = useInViewport(0.2);

  // Scratch-off animation scroll tracking
  const scratchRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: scratchRef,
    offset: ["start start", "end end"]
  });

  // Map scroll to scratch animation - starts after text is centered
  const scratchProgress = useTransform(scrollYProgress, [0.4, 0.7], [0, 1]);

  return (
    <section className="tmf-journey">
      <div className="tmf-container">
        {/* Scratch-off Intro - Tall scroll container */}
        <div ref={scratchRef} className="tmf-journey-scratch-intro">
          <div className="tmf-scratch-sticky-wrapper">
            <div className="tmf-scratch-content">
              {/* Initial text with CSS strikethrough */}
              <motion.p
                className="tmf-scratch-wrong"
                style={{
                  textDecoration: 'line-through',
                  textDecorationColor: useTransform(scratchProgress, [0, 1], ['rgba(255, 68, 68, 0)', 'rgba(255, 68, 68, 1)']),
                  textDecorationThickness: '4px'
                }}
              >
                So how did we approach this problem? It was simple. We started by searching on the internet and Reddit for insights...
              </motion.p>

              {/* Corrected text */}
              <motion.p
                className="tmf-scratch-correct"
                style={{
                  opacity: scratchProgress,
                  y: useTransform(scratchProgress, [0, 1], [20, 0])
                }}
              >
                <span className="tmf-scratch-label">Hold up, scratch that.</span> Let's actually show up to the pantry itself and see what's really happening.
              </motion.p>
            </div>
          </div>
        </div>

        {/* Section Title */}
        <motion.div
          ref={titleRef}
          initial={{ opacity: 0, y: 50 }}
          animate={titleInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="tmf-section-header"
        >
          <h2 className="tmf-section-title">The Journey</h2>
          <p className="tmf-section-intro">
            We visited Southeast Community Services on a regular operational day and volunteered alongside staff. We wanted to see the pantry in action during real operations, not just observe from the outside. <span className="tmf-highlight-accent">Our goal: identify where gaps exist and how we could help connect people to the services they need.</span>
          </p>
        </motion.div>

        {/* Visit Gallery - Asymmetrical Grid + Text Points */}
        <div className="tmf-visit-gallery-split">
          {/* Left: Asymmetrical Image Grid */}
          <motion.div
            className="tmf-visit-images-grid"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="tmf-grid-image tmf-grid-1">
              <img
                src="/content/tmf data/Site Pictures/Site Pictures Cover Sept 18 2025.jpg"
                alt="Food pantry site visit"
              />
            </div>
            <div className="tmf-grid-image tmf-grid-2">
              <img
                src="/content/tmf data/Site Pictures/IMG_2062.JPG"
                alt="Community members"
              />
            </div>
            <div className="tmf-grid-image tmf-grid-3">
              <img
                src="/content/tmf data/Site Pictures/Site Pictures Cover Sept 18 2025 (1).jpg"
                alt="Pantry operations"
              />
            </div>
            <div className="tmf-grid-image tmf-grid-4">
              <img
                src="/content/tmf data/Site Pictures/Site Picture 7070.jpg"
                alt="Site visit documentation"
              />
            </div>
            <div className="tmf-grid-image tmf-grid-5">
              <img
                src="/content/tmf data/Site Pictures/Site Picture 7071.jpg"
                alt="Research team at pantry"
              />
            </div>
          </motion.div>

          {/* Right: Text Points */}
          <motion.div
            className="tmf-visit-objectives"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h3>Our Field Research Focus</h3>
            <ul className="tmf-objectives-list">
              <li>
                <span className="tmf-objective-title">Access Patterns</span>
                <p>How do people find out about the pantry? Do they book appointments or walk in? We mapped out the physical layout and watched how operations flowed during the busy distribution hours.</p>
              </li>
              <li>
                <span className="tmf-objective-title">Interaction Points</span>
                <p>We looked for moments of connection: where volunteers and pantry users interact, what those conversations look like, and how community members connect with each other in the space.</p>
              </li>
              <li>
                <span className="tmf-objective-title">User Behavior & Mobility</span>
                <p>How do people get here? What do they pick? How do they carry everything home? We watched for patterns in transportation, food selection, and the creative ways people get their groceries back.</p>
              </li>
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default JourneySection;
